<script setup lang="ts">
import { ref, computed, watch, defineProps, defineEmits } from 'vue';
import type { BaseInputPhoneProps, BaseInputPhoneEmits } from './BaseInputPhone.types';

const props = defineProps<BaseInputPhoneProps>();
const emits = defineEmits<BaseInputPhoneEmits>();

const countryCode = `+${props.countryCode.replace(/^\+/, '')} `;
const countryCodeDigits = props.countryCode.replace(/\D/g, '');
const countryCodeLength = countryCode.length;

const phoneNumberRaw = ref(props.modelValue ? formatPhoneNumber(props.modelValue) : '');

watch(
  () => props.modelValue,
  newVal => {
    if (newVal && !phoneNumberRaw.value.endsWith(newVal)) {
      phoneNumberRaw.value = formatPhoneNumber(newVal);
    }
  },
);

const formattedPhoneNumber = computed({
  get() {
    return phoneNumberRaw.value;
  },
  set(value) {
    phoneNumberRaw.value = formatPhoneNumber(value);
  },
});

watch(phoneNumberRaw, newVal => {
  const digitsOnly = newVal.replace(/\D/g, '');

  let digits = digitsOnly.replace(/^0+/, '');

  if (digits.startsWith(countryCodeDigits)) {
    digits = digits.slice(countryCodeDigits.length);
  }

  const nationalNumber = digits.slice(0, 8);

  const fullNumber = countryCodeDigits + nationalNumber;
  emits('update:modelValue', fullNumber);

  const isCompleted = nationalNumber.length === 8;
  emits('update:completed', isCompleted);
});

function formatPhoneNumber(value: string): string {
  let digits = value.replace(/\D/g, '');

  digits = digits.replace(/^0+/, '');

  if (digits.startsWith(countryCodeDigits)) {
    digits = digits.slice(countryCodeDigits.length);
  }

  digits = digits.slice(0, 8);

  let formattedNumber = digits;
  if (digits.length > 4) {
    formattedNumber = `${digits.slice(0, 4)} ${digits.slice(4)}`;
  }

  return `${countryCode}${formattedNumber}`;
}

function onInput(event: Event) {
  const input = event.target as HTMLInputElement;
  let value = input.value;

  if (value.startsWith(countryCode)) {
    value = value.slice(countryCode.length);
  }

  let digits = value.replace(/\D/g, '');

  digits = digits.replace(/^0+/, '');

  digits = digits.slice(0, 8);

  phoneNumberRaw.value = formatPhoneNumber(digits);

  setCursorPosition(input);
}

function onFocus(event: Event) {
  const input = event.target as HTMLInputElement;
  if (!phoneNumberRaw.value || phoneNumberRaw.value === '') {
    phoneNumberRaw.value = countryCode;
    input.setSelectionRange(countryCodeLength, countryCodeLength);
  } else {
    setCursorPosition(input);
  }
}

function onBlur() {
  if (phoneNumberRaw.value === countryCode) {
    phoneNumberRaw.value = '';
  }
}

function onKeydown(event: KeyboardEvent) {
  const input = event.target as HTMLInputElement;

  if (
    (event.key === 'Backspace' && input.selectionStart! <= countryCodeLength) ||
    (event.key === 'Delete' && input.selectionStart! < countryCodeLength)
  ) {
    event.preventDefault();
    input.setSelectionRange(countryCodeLength, countryCodeLength);
  } else if (
    !/^\d$/.test(event.key) &&
    !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(event.key)
  ) {
    event.preventDefault();
  }
}

function onMouseDown(event: MouseEvent) {
  const input = event.target as HTMLInputElement;
  setTimeout(() => {
    if (input.selectionStart! < countryCodeLength) {
      input.setSelectionRange(countryCodeLength, countryCodeLength);
    }
  }, 0);
}

function setCursorPosition(input: HTMLInputElement) {
  if (input.selectionStart! < countryCodeLength) {
    input.setSelectionRange(countryCodeLength, countryCodeLength);
  }
}

const clearInput = () => {
  phoneNumberRaw.value = '';
};
</script>

<template>
  <div class="base-input-phone">
    <div
      :class="['base-input-phone__input', { 'base-input-phone__input--error': errors.length > 0 }]"
    >
      <img src="./assets/flag-qatar.svg" alt="" class="base-input-phone__flag" />
      <input
        type="text"
        v-model="formattedPhoneNumber"
        inputmode="numeric"
        :placeholder="`${countryCode}0000 0000`"
        :maxlength="countryCode.length + 9"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown="onKeydown"
        @mousedown="onMouseDown"
        class="base-input-phone__input-field"
      />
      <transition name="fade">
        <img
          src="./assets/close.svg"
          class="base-input-phone__remove"
          v-if="phoneNumberRaw && !completed"
          @click="clearInput"
        />
      </transition>
      <transition name="fade">
        <img
          src="./assets/check.svg"
          v-if="completed && !errors.length"
          class="base-input-phone__check"
        />
      </transition>
    </div>
    <div v-if="errors.length" class="base-input-phone__errors">
      <div v-for="error in errors" :key="error" class="base-input-phone__error">
        {{ error }}
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import './BaseInputPhone.scss';
</style>